import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import {
    ModalWrapper,
    Expandable,
    TitleWrapper,
    TitleText,
    InfoText,
    ProductList,
    ProductItem,
    ProductName,
    QuantityInput,
    ButtonRow,
    StyledButton,
    StyledBackdrop
} from './Styled';

interface ReturnQuantityModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (returnedQuantities: { [key: string]: number }, status: string) => void;
    products: Array<{
        id: string;
        quantity: number;
        returned_quantity?: number;
        name: string;
        product?: {
            id: string;
            name: string;
        };
        material?: {
            id: string;
            name: string;
        };
    }>;
}

const ReturnQuantityModal = ({ open, onClose, onConfirm, products }: ReturnQuantityModalProps) => {
    const [returnedQuantities, setReturnedQuantities] = useState<{ [key: string]: number }>({});

    // Initialize returned quantities when modal opens
    useEffect(() => {
        if (open) {
            const initialQuantities = products.reduce((acc, product) => {
                const productId = product.product?.id || product.material?.id || product.id;
                return {
                    ...acc,
                    [productId]: product.returned_quantity || 0
                };
            }, {});
            setReturnedQuantities(initialQuantities);
        }
    }, [open, products]);

    const handleQuantityChange = (productId: string, value: string) => {
        const numValue = parseInt(value) || 0;
        
        // Find the product to get its max quantity
        const product = products.find(
            (p) => p.product?.id === productId || p.material?.id === productId || p.id === productId
        );
        if (!product) return;

        // Ensure the value doesn't exceed the available quantity
        const maxQuantity = product.quantity;
        const validatedValue = Math.min(Math.max(0, numValue), maxQuantity);
        
        setReturnedQuantities(prev => ({
            ...prev,
            [productId]: validatedValue
        }));
    };

    const handleConfirm = () => {
        onConfirm(returnedQuantities, "Declined");
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={StyledBackdrop}
            BackdropProps={{
                timeout: 500,
                onClick: (e) => e.stopPropagation(),
            }}
            sx={{
                "& .MuiPaper-root": {
                    width: "600px",
                    maxWidth: "90%",
                    margin: "0 auto",
                },
            }}
        >
            <ModalWrapper isOpen={open}>
                <Expandable>
                    <TitleWrapper>
                        <TitleText>Nhập số lượng trả lại</TitleText>
                        <InfoText>Vui lòng nhập số lượng sản phẩm trả lại cho mỗi mặt hàng</InfoText>
                    </TitleWrapper>

                    <ProductList>
                        {products.map((product) => {
                            const productId = product.product?.id || product.material?.id || product.id;
                            return (
                                <ProductItem key={productId}>
                                    <ProductName>
                                        {product.name || 'Unknown'}
                                        <span style={{ color: '#666', marginLeft: '8px' }}>
                                            (Tổng: {product.quantity})
                                        </span>
                                    </ProductName>
                                    <QuantityInput
                                        type="number"
                                        min="0"
                                        max={product.quantity}
                                        value={returnedQuantities[productId] || 0}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuantityChange(productId, e.target.value)}
                                        data-product-id={productId}
                                    />
                                </ProductItem>
                            );
                        })}
                    </ProductList>
                </Expandable>

                <ButtonRow>
                    <StyledButton onClick={onClose}>Hủy</StyledButton>
                    <StyledButton variant="contained" onClick={handleConfirm}>
                        Xác nhận
                    </StyledButton>
                </ButtonRow>
            </ModalWrapper>
        </Modal>
    );
};

export default ReturnQuantityModal; 